import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Ticket, RefreshCw, Copy, Lock, TrendingUp, Zap, BookmarkPlus, Share2 } from 'lucide-react';
import Navigation from '@/components/Navigation';
import { shareCardImage } from '@/lib/cardImage';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CARD_META = {
  lock: { icon: Lock, accent: 'border-brand-lime', badge: 'LOCK OF THE DAY', badgeCls: 'bg-green-500/20 text-green-400 border-green-500/30' },
  single: { icon: TrendingUp, accent: 'border-brand-cyan', badge: 'SINGLE SPORT', badgeCls: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30' },
  mixed: { icon: Zap, accent: 'border-orange-400', badge: 'MIXED SPORTS', badgeCls: 'bg-orange-500/20 text-orange-400 border-orange-500/30' },
};

const metaFor = (key) => key === 'lock' ? CARD_META.lock : key?.startsWith('single') ? CARD_META.single : CARD_META.mixed;

const gradeColor = (g) => g?.startsWith('A') ? 'text-brand-lime' : g?.startsWith('B') ? 'text-brand-cyan' : 'text-yellow-400';

const PropsPicks = () => {
  const [data, setData] = useState(null);
  const [rec, setRec] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchData = async () => {
    setRefreshing(true);
    try {
      const res = await axios.get(`${API}/props-picks`);
      setData(res.data);
      axios.get(`${API}/props-picks/record`).then(r => setRec(r.data)).catch(() => {});
    } catch (e) { console.error(e); }
    finally { setLoading(false); setRefreshing(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const saveSlip = async (card) => {
    try {
      await axios.post(`${API}/bets`, {
        title: card.title,
        legs: card.legs.map(l => ({ player: l.player, prop: l.prop, odds: l.odds })),
        combined_odds: card.combined_odds,
        stake: card.stake,
        source: 'props_picks',
      }, { withCredentials: true });
      toast.success('Slip saved to My Bets');
    } catch (e) {
      toast.error(e.response?.status === 401 ? 'Log in to save slips' : 'Save failed');
    }
  };

  const shareCard = async (card) => {
    try {
      const result = await shareCardImage(card, data?.date);
      if (result === 'downloaded') toast.success('Card image downloaded — ready to post');
      else if (result === 'shared') toast.success('Card shared');
    } catch (e) {
      toast.error('Could not create image');
    }
  };

  const copySlip = (card) => {
    const lines = [
      `🎟️ ${card.title} — freeprops.app (${data.date})`,
      ...card.legs.map((l, i) => `${i + 1}. ${l.player} (${l.team}, ${l.sport}) — ${l.prop} [${l.odds}]`),
      `Combined Odds: ${card.combined_odds} | Implied Hit Rate: ${card.implied_hit_rate} | Grade: ${card.grade}`,
      card.stake > 0 ? `Stake: $${card.stake} → Projected Payout: $${card.projected_payout.toLocaleString()}` : '',
    ].filter(Boolean);
    navigator.clipboard.writeText(lines.join('\n'))
      .then(() => toast.success('Bet slip copied to clipboard'))
      .catch(() => toast.error('Copy failed'));
  };

  return (
    <div className="min-h-screen bg-[#0a1628]">
      <Navigation />
      <main className="pt-20 pb-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-between gap-4 mb-8 flex-wrap">
            <div>
              <h1 className="font-heading text-3xl sm:text-4xl font-bold text-white uppercase tracking-tight flex items-center gap-3">
                <Ticket className="h-8 w-8 text-brand-lime" />
                Free Props Picks
              </h1>
              <p className="text-muted-foreground mt-1">
                Lock of the Day + 10 auto-built parlays scanned across every sport — refreshed 6:00 AM EST
              </p>
            </div>
            <Button onClick={fetchData} variant="outline" className="border-[#1e3a5f] text-white hover:bg-[#112240]" disabled={refreshing} data-testid="refresh-props-picks-btn">
              <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>

          {data?.date && (
            <div className="mb-6 flex items-center gap-2 flex-wrap">
              <Badge variant="outline" className="border-brand-cyan text-brand-cyan">{data.date}</Badge>
              <span className="text-xs text-muted-foreground">Sorted lowest risk → highest payout • Gambling involves risk, bet responsibly</span>
            </div>
          )}

          {rec && (rec.record?.hits + rec.record?.misses > 0 || rec.yesterday?.graded) && (
            <Card className="bg-[#0f1e36] border-[#1e3a5f] mb-6" data-testid="results-strip">
              <CardContent className="p-4">
                <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
                  <span className="font-heading text-sm font-bold text-white uppercase">Yesterday's Results {rec.yesterday?.date && `(${rec.yesterday.date})`}</span>
                  <Badge variant="outline" className="border-brand-cyan text-brand-cyan">
                    Engine Record: {rec.record.hits}-{rec.record.misses}
                  </Badge>
                </div>
                {rec.yesterday?.graded ? (
                  <div className="flex flex-wrap gap-2">
                    {rec.yesterday.cards.map(c => (
                      <span key={c.key} className={`text-[11px] px-2.5 py-1 rounded-full border font-bold ${
                        c.result === 'HIT' ? 'border-brand-lime/50 text-brand-lime bg-brand-lime/10'
                        : c.result === 'MISS' ? 'border-red-400/50 text-red-400 bg-red-400/10'
                        : 'border-[#1e3a5f] text-muted-foreground'}`} data-testid={`result-${c.key}`}>
                        {c.title}: {c.result} {c.result !== 'VOID' && `(${c.legs.filter(l => l.result === 'hit').length}/${c.legs.filter(l => l.result === 'hit' || l.result === 'miss').length} legs)`}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground">No graded slate for yesterday yet.</p>
                )}
              </CardContent>
            </Card>
          )}

          {loading ? (
            <div className="space-y-6">{[1, 2, 3].map(i => <div key={i} className="h-64 skeleton rounded-lg" />)}</div>
          ) : !data?.cards?.length ? (
            <Card className="bg-[#0f1e36] border-[#1e3a5f]"><CardContent className="p-12 text-center text-muted-foreground">No picks available yet — check back after 6:00 AM EST.</CardContent></Card>
          ) : (
            <div className="space-y-6">
              {['lock', 'single', 'mixed'].map(section => {
                const sectionCards = data.cards.filter(c =>
                  section === 'lock' ? c.key === 'lock' : c.key.startsWith(section)
                );
                if (!sectionCards.length) return null;
                return (
                  <div key={section}>
                    {section !== 'lock' && (
                      <h2 className="font-heading text-lg font-bold text-white uppercase tracking-wide mb-4 mt-2" data-testid={`section-${section}`}>
                        {section === 'single' ? 'Single-Sport Parlays' : 'Mixed-Sport Parlays'}
                      </h2>
                    )}
                    <div className="space-y-6">
                      {sectionCards.map((card, ci) => {
                const meta = metaFor(card.key);
                const Icon = meta.icon;
                return (
                  <Card key={card.key} className={`bg-[#0f1e36] border-[#1e3a5f] border-l-4 ${meta.accent} animate-slide-up`} style={{ animationDelay: `${ci * 0.06}s` }} data-testid={`picks-card-${card.key}`}>
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
                        <div className="flex items-center gap-3">
                          <div className="p-2.5 rounded-lg bg-[#0a1628]">
                            <Icon className="h-6 w-6 text-brand-lime" />
                          </div>
                          <div>
                            <h2 className="font-heading text-xl sm:text-2xl font-bold text-white uppercase">{card.title}</h2>
                            {card.stake > 0 && (
                              <p className="text-xs text-muted-foreground">
                                ${card.stake} returns <span className="text-brand-lime font-mono font-bold">${card.projected_payout.toLocaleString()}</span>
                              </p>
                            )}
                          </div>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-[10px] font-bold border ${meta.badgeCls}`}>{meta.badge}</span>
                      </div>

                      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-4">
                        <div className="bg-[#0a1628] rounded p-3 text-center">
                          <div className="text-[10px] text-muted-foreground uppercase">AI Grade</div>
                          <div className={`font-heading text-2xl font-bold ${gradeColor(card.grade)}`} data-testid={`grade-${card.key}`}>{card.grade}</div>
                        </div>
                        <div className="bg-[#0a1628] rounded p-3 text-center">
                          <div className="text-[10px] text-muted-foreground uppercase">Matchup Grade</div>
                          <div className={`font-heading text-2xl font-bold ${gradeColor(card.matchup_grade)}`} data-testid={`matchup-grade-${card.key}`}>{card.matchup_grade || '—'}</div>
                        </div>
                        <div className="bg-[#0a1628] rounded p-3 text-center">
                          <div className="text-[10px] text-muted-foreground uppercase">Combined Odds</div>
                          <div className="font-mono text-xl font-bold text-brand-cyan">{card.combined_odds}</div>
                        </div>
                        <div className="bg-[#0a1628] rounded p-3 text-center">
                          <div className="text-[10px] text-muted-foreground uppercase">Implied Hit Rate</div>
                          <div className="font-mono text-xl font-bold text-white">{card.implied_hit_rate}</div>
                        </div>
                        <div className="bg-[#0a1628] rounded p-3 text-center col-span-2 sm:col-span-1">
                          <div className="text-[10px] text-muted-foreground uppercase">Sharp Edge Δ</div>
                          <div className={`font-mono text-xl font-bold ${card.sharp_edge_delta?.startsWith('+') && !card.sharp_edge_delta?.startsWith('+-') ? 'text-brand-lime' : 'text-muted-foreground'}`}>{card.sharp_edge_delta || 'N/A'}</div>
                        </div>
                      </div>

                      {card.trend_summary && (
                        <div className="bg-[#0a1628] rounded px-3 py-2 mb-4 text-xs text-brand-cyan" data-testid={`trend-${card.key}`}>
                          📈 {card.trend_summary}
                        </div>
                      )}

                      <div className="space-y-2 mb-4">
                        {card.legs.map((l, li) => (
                          <div key={li} className="flex items-center justify-between gap-3 bg-[#0a1628] rounded px-3 py-2" data-testid={`leg-${card.key}-${li}`}>
                            <div className="flex items-center gap-2 min-w-0">
                              <span className="text-xs font-mono text-muted-foreground shrink-0">{li + 1}.</span>
                              {l.headshot ? (
                                <img src={l.headshot} alt={l.player} className="w-8 h-8 rounded-full bg-[#112240] object-cover border border-brand-cyan shrink-0"
                                  onError={(e) => { e.target.style.display = 'none'; }} />
                              ) : (
                                <span className="text-sm">{l.sport === 'WNBA' || l.sport === 'NBA' ? '🏀' : l.sport === 'NFL' ? '🏈' : l.sport === 'NHL' ? '🏒' : '⚾'}</span>
                              )}
                              <div className="min-w-0">
                                <span className="text-white font-medium text-sm">{l.player}</span>
                                <span className="text-muted-foreground text-xs ml-2">{l.team}</span>
                                {l.venue && <span className="text-[9px] text-muted-foreground ml-2 border border-[#1e3a5f] rounded px-1">{l.venue}</span>}
                                {l.matchup_grade && <span className={`text-[9px] ml-1 border border-[#1e3a5f] rounded px-1 ${gradeColor(l.matchup_grade)}`}>DvP {l.matchup_grade}</span>}
                                {l.edge_pct != null && l.edge_pct > 0 && <span className="text-[9px] text-brand-lime ml-1 border border-brand-lime/40 rounded px-1">+{l.edge_pct}% edge</span>}
                                <div className="text-xs text-brand-cyan truncate">{l.prop}</div>
                              </div>
                            </div>
                            <span className="font-mono font-bold text-brand-lime text-sm shrink-0">{l.odds}{l.book ? <span className="text-muted-foreground text-[10px] font-normal ml-1">@ {l.book}</span> : null}</span>
                          </div>
                        ))}
                      </div>

                      <p className="text-xs text-muted-foreground italic mb-4">{card.rationale}</p>

                      <div className="flex flex-wrap gap-2">
                        <Button
                          onClick={() => copySlip(card)}
                          className="bg-brand-cyan text-[#0a1628] hover:bg-brand-cyan/90 font-bold uppercase tracking-wider"
                          data-testid={`copy-slip-${card.key}`}
                        >
                          <Copy className="h-4 w-4 mr-2" />
                          Copy Bet Slip
                        </Button>
                        <Button
                          onClick={() => saveSlip(card)}
                          variant="outline"
                          className="border-brand-lime/50 text-brand-lime hover:bg-brand-lime/10 font-bold uppercase tracking-wider"
                          data-testid={`save-slip-${card.key}`}
                        >
                          <BookmarkPlus className="h-4 w-4 mr-2" />
                          Save Slip
                        </Button>
                        <Button
                          onClick={() => shareCard(card)}
                          variant="outline"
                          className="border-brand-cyan/50 text-brand-cyan hover:bg-brand-cyan/10 font-bold uppercase tracking-wider"
                          data-testid={`share-card-${card.key}`}
                        >
                          <Share2 className="h-4 w-4 mr-2" />
                          Share
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default PropsPicks;
